/* <scroll-carousel> — native scroll-snap track with prev/next buttons + dots.
   Reused by meal / review / grocery / recipe scrollers.

   Expected markup (selectors are scoped inside the element):
     <scroll-carousel>
       <button data-carousel-prev>…</button>
       <button data-carousel-next>…</button>
       <div data-carousel-track> … slides … </div>
       <div data-carousel-dots></div>   (optional)
     </scroll-carousel>

   Behavior is progressive: without JS the track still scrolls (native overflow),
   only the arrow buttons and dots are inert. */

if (!customElements.get("scroll-carousel")) {
  // Pixels the pointer must travel before a press counts as a drag (not a click).
  const DRAG_THRESHOLD = 6;
  const preventDefault = (e) => e.preventDefault();
  const swallowClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  customElements.define(
    "scroll-carousel",
    class ScrollCarousel extends HTMLElement {
      constructor() {
        super();
        this.onScroll = this.onScroll.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onResize = (window.allplants?.debounce || ((f) => f))(
          this.setup.bind(this),
          150
        );
      }

      connectedCallback() {
        this.track = this.querySelector("[data-carousel-track]");
        this.prev = this.querySelector("[data-carousel-prev]");
        this.next = this.querySelector("[data-carousel-next]");
        this.dots = this.querySelector("[data-carousel-dots]");
        if (!this.track) return;

        this.prev?.addEventListener("click", () => this.scrollByStep(-1));
        this.next?.addEventListener("click", () => this.scrollByStep(1));
        this.track.addEventListener("scroll", this.onScroll, { passive: true });
        window.addEventListener("resize", this.onResize);
        this.enableDrag();
        this.setup();
      }

      disconnectedCallback() {
        this.track?.removeEventListener("scroll", this.onScroll);
        this.track?.removeEventListener("pointerdown", this.onPointerDown);
        this.track?.removeEventListener("dragstart", preventDefault);
        window.removeEventListener("pointermove", this.onPointerMove);
        window.removeEventListener("pointerup", this.onPointerUp);
        window.removeEventListener("resize", this.onResize);
      }

   
      enableDrag() {
        this.dragging = false; // a press is in progress
        this.moved = false; // …and it has crossed the drag threshold
        this.startX = 0;
        this.startScroll = 0;
        this.track.addEventListener("pointerdown", this.onPointerDown);
        this.track.addEventListener("dragstart", preventDefault);
      }

      onPointerDown(e) {
        if (e.pointerType === "touch") return; // native touch scroll handles it
        if (e.button !== 0) return; // primary button only
        this.dragging = true;
        this.moved = false;
        this.startX = e.clientX;
        this.startScroll = this.track.scrollLeft;
        // Listen on window so the drag survives the pointer leaving the track.
        window.addEventListener("pointermove", this.onPointerMove);
        window.addEventListener("pointerup", this.onPointerUp);
      }

      onPointerMove(e) {
        if (!this.dragging) return;
        const dx = e.clientX - this.startX;
        // Ignore micro-movements so a normal click still reads as a click.
        if (!this.moved && Math.abs(dx) < DRAG_THRESHOLD) return;
        this.moved = true;
        this.track.classList.add("is-dragging");
        this.track.scrollLeft = this.startScroll - dx;
      }

      onPointerUp() {
        if (!this.dragging) return;
        this.dragging = false;
        this.track.classList.remove("is-dragging");
        window.removeEventListener("pointermove", this.onPointerMove);
        window.removeEventListener("pointerup", this.onPointerUp);
        if (this.moved) {
        
          this.track.addEventListener("click", swallowClick, {
            capture: true,
            once: true,
          });
        }
      }

      /** Width of one slide + the flex gap, in px. */
      get step() {
        const first = this.track.children[0];
        if (!first) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }

  
      get perPage() {
        const fit = Math.max(1, Math.floor(this.track.clientWidth / this.step));
        // Optional cap (e.g. data-per-page="4"); only bites where more would fit,
        // so smaller viewports stay responsive.
        const cap = parseInt(this.dataset.perPage, 10);
        return cap > 0 ? Math.min(fit, cap) : fit;
      }

      /** Width the track advances per page, in px. */
      get pageStep() {
        return this.perPage * this.step;
      }

      get pages() {
        // One dot per page, capped so very long lists don't overflow.
        const total = Math.ceil(this.track.children.length / this.perPage);
        return Math.min(total, 12);
      }

      setup() {
        this.buildDots();
        this.update();
      }

      buildDots() {
        if (!this.dots) return;
        const count = this.pages;
        // Hide dots entirely when nothing scrolls.
        const scrollable = this.track.scrollWidth - this.track.clientWidth > 4;
        if (!scrollable || count <= 1) {
          this.dots.innerHTML = "";
          this.dots.hidden = true;
          return;
        }
        this.dots.hidden = false;
        if (this.dots.children.length === count) return; // already built
        this.dots.innerHTML = "";
        for (let i = 0; i < count; i += 1) {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.className = "scroll-dot";
          dot.setAttribute("aria-label", `Go to item ${i + 1}`);
          dot.addEventListener("click", () => {
            this.track.scrollTo({ left: i * this.pageStep, behavior: "smooth" });
          });
          this.dots.appendChild(dot);
        }
      }
      

      scrollByStep(dir) {
        this.track.scrollBy({ left: dir * this.pageStep, behavior: "smooth" });
      }

      onScroll() {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this.update();
        });
      }

      update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const max = scrollWidth - clientWidth;
        const atStart = scrollLeft <= 2;
        const atEnd = scrollLeft >= max - 2;
        const scrollable = max > 4;

        this.prev?.toggleAttribute("disabled", atStart || !scrollable);
        this.next?.toggleAttribute("disabled", atEnd || !scrollable);

        if (this.dots && !this.dots.hidden) {
          const last = this.dots.children.length - 1;
          // At the end the track can't scroll a full pageStep, so rounding lands
          // short — pin the last dot whenever we've hit the end.
          const idx = atEnd
            ? last
            : Math.min(Math.round(scrollLeft / this.pageStep), last);
          [...this.dots.children].forEach((d, i) =>
            d.classList.toggle("is-active", i === idx)
          );
        }
      }
    }
  );
}
